'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import styles from './Header/Header.module.scss'
import { usePageNavigation } from '@/hooks/usePageNavigation'
import { scrollToWithLenis } from '@/components/LenisProvider'

// Safe hook that won't break if preloader context is missing
function useSafePageNavigation() {
  try {
    return usePageNavigation()
  } catch {
    return null
  }
}

interface MenuItem {
  id: string
  label: string
  slug: string
}

interface HeaderProps {
  menuItems?: MenuItem[]
}

export default function Header({ menuItems = [] }: HeaderProps) {
  const pathname = usePathname()
  const router = useRouter()
  const pageNavigation = useSafePageNavigation()

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(href + '/')
  }

  // Navigation with preloader if available, fallback if not
  const handleNavigation = async (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === pathname) return // Don't navigate to current page
    
    e.preventDefault()
    
    // Use preloader navigation if available
    if (pageNavigation?.navigateToPage) {
      try {
        await pageNavigation.navigateToPage(href, pathname)
        return
      } catch {
        // Fall through to standard navigation
      }
    }
    
    // Fallback to standard navigation with smooth scroll
    router.push(href, { scroll: false })
    setTimeout(() => {
      // Try to use Lenis for smooth scroll to top
      const lenisSuccess = scrollToWithLenis(0, { duration: 1.2 });
      
      // Fallback to native smooth scroll if Lenis is not available
      if (!lenisSuccess) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    }, 100)
  }

  return (
    <header className={styles.header}>
      <Link 
        href="/" 
        className={styles.header__logo}
        onClick={(e) => handleNavigation(e, '/')}
        aria-label="Gather Productions - Go to homepage"
      >
        <svg version="1.1" viewBox="0 0 32 25" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.9,25h-.8c-1.9-.3-2.5-2.1-2.8-3.8-1.1,1.8-3,3.1-5,3.6-.4,0-.8.1-1.2.2h-1.6l-1.8-.4c-3.3-1-5.7-3.6-6.3-6.9,0-.5-.1-.9-.2-1.4,0-.6,0-1.2,0-1.8.2-2.7,1.4-5.6,3.1-7.7,1.6-2.1,6.1-6.1,8.8-4,1.5,1.2,2.2,3.6.4,4.8-.6.4-1.3.6-2,.9-2.9,1.3-5.1,4.3-4.6,7.5.4,2.2,2,3.8,4.3,3.5.6,0,1.6-.7,1.2-1.4-.1-.3-1.3-.5-1.7-.9-1.3-1.2-.7-4.1,1-4.7,2-.8,4.6.4,5.8,2,.1-2.2,0-4.4.1-6.6,0-.2,0-.3,0-.5.1-2.8-.4-7,3.4-7.3,3.3-.2,8.5.5,10.5,3.5,2.4,3.7,0,7.2-3.3,9.1-1.7.9-3.8.9-4.4,3-.8,2.5.9,8.9-2.8,9.3h0ZM23.8,4.5c-.6,0-.8.6-.9,1.1,0,.4,0,.9,0,1.3,0,.9-.1,2.3,0,3.1.1.9,1.9.2,2.4-.2,1.5-1.2,1-4-.6-5-.2-.1-.6-.3-.9-.3ZM27.6,20.1c.9-.1,1.8.6,2.1,1.3.9,1.9-2.8,4.6-4,2.3-.7-1.4.4-3.4,1.9-3.6h0Z"/>
        </svg>
      </Link>

      <nav className={styles.header__nav}>
        {menuItems.map((item) => {
          const href = `/${item.slug}`
          return (
            <Link 
              key={item.id}
              href={href}
              className={`${styles.header__navLink} ${isActive(href) ? styles.header__navLink__active : ''}`}
              onClick={(e) => handleNavigation(e, href)}
            >
              {item.label}
            </Link>
          )
        })}
      </nav>
    </header>
  )
}
